import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import {
    useArchivePromptMutation,
    useGetPromptByIdQuery,
    useGetPromptHistoryQuery,
    useLazyGetPromptHistoryVersionQuery,
    usePublishPromptMutation,
    useRestorePromptVersionMutation,
    useUnarchivePromptMutation,
    useUpdatePromptMutation,
} from '../../../store/prompts/promptsApi';
import { usePrompts } from './PromptsContext';
import {
    MODEL_OPTIONS,
    PROVIDER_OPTIONS,
    canShowPublishAction,
    getPromptId,
    getPromptStatusBadgeClass,
    getPromptStatusLabel,
    flattenPromptChanges,
    resolvePromptChanges,
    IPromptHistoryEntry,
    IUpdatePromptPayload,
} from './types';
import VersionHistory from './components/VersionHistory';
import ConfirmActionModal from './components/ConfirmActionModal';
import PromptChangesDiff from './components/PromptChangesDiff';
import Modal from '../../../shared/modal';

interface EditFormValues {
    name: string;
    description: string;
    systemPrompt: string;
    prompt: string;
    allowedProviders: string[];
    allowedModels: string[];
    temperature: number;
    maxTokens: number;
    topP: number;
}

type ConfirmAction = 'publish' | 'archive' | 'unarchive' | 'restore' | null;
type DetailTab = 'editor' | 'history' | 'settings';

const TABS: { id: DetailTab; label: string }[] = [
    { id: 'editor', label: 'Editor' },
    { id: 'history', label: 'History' },
    { id: 'settings', label: 'Settings' },
];

const PromptDetailPage = () => {
    const { promptId = '' } = useParams<{ promptId: string }>();
    const navigate = useNavigate();
    const { canEdit, canPublish, canArchive } = usePrompts();

    const [activeTab, setActiveTab] = useState<DetailTab>('editor');
    const [historyPage, setHistoryPage] = useState(1);
    const [historyRows, setHistoryRows] = useState<IPromptHistoryEntry[]>([]);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);
    const [historyEpoch, setHistoryEpoch] = useState(0);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
    const [restoreVersion, setRestoreVersion] = useState<number | null>(null);
    const [viewedVersion, setViewedVersion] = useState<IPromptHistoryEntry | null>(null);
    const [viewLoading, setViewLoading] = useState(false);

    const {
        data: prompt,
        isLoading,
        isError,
        error,
    } = useGetPromptByIdQuery(promptId, { skip: !promptId });

    const {
        data: historyData,
        isLoading: historyLoading,
        isFetching: historyFetching,
    } = useGetPromptHistoryQuery(
        { id: promptId, page: historyPage, size: 10 },
        { skip: !promptId },
    );

    const resetHistory = () => {
        setHistoryPage(1);
        setHistoryRows([]);
        setHasMoreHistory(true);
        setHistoryEpoch((prev) => prev + 1);
    };

    useEffect(() => {
        resetHistory();
    }, [promptId]);

    useEffect(() => {
        if (!historyData) return;
        const incoming = historyData.elements ?? [];
        setHistoryRows((prev) => {
            if (historyPage === 1) return incoming;
            const existing = new Set(
                prev.map((item) => item._id || `v-${item.version}`),
            );
            const unique = incoming.filter(
                (item) => !existing.has(item._id || `v-${item.version}`),
            );
            return [...prev, ...unique];
        });
        setHasMoreHistory(Boolean(historyData.page?.hasNextPage));
    }, [historyData, historyPage, historyEpoch]);

    const onLoadMoreHistory = () => {
        if (!hasMoreHistory || historyLoading || historyFetching) return;
        setHistoryPage((prev) => prev + 1);
    };

    const [updatePrompt, { isLoading: saving }] = useUpdatePromptMutation();
    const [publishPrompt, { isLoading: publishing }] = usePublishPromptMutation();
    const [archivePrompt, { isLoading: archiving }] = useArchivePromptMutation();
    const [unarchivePrompt, { isLoading: unarchiving }] = useUnarchivePromptMutation();
    const [restorePromptVersion, { isLoading: restoring }] = useRestorePromptVersionMutation();
    const [fetchHistoryVersion] = useLazyGetPromptHistoryVersionQuery();

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { isDirty, errors },
    } = useForm<EditFormValues>({
        defaultValues: {
            name: '',
            description: '',
            systemPrompt: '',
            prompt: '',
            allowedProviders: [],
            allowedModels: [],
            temperature: 0.2,
            maxTokens: 4000,
            topP: 1,
        },
    });

    useEffect(() => {
        if (!prompt) return;
        reset({
            name: prompt.name || '',
            description: prompt.description || '',
            systemPrompt: prompt.systemPrompt || '',
            prompt: prompt.prompt || '',
            allowedProviders: prompt.allowedProviders || [],
            allowedModels: prompt.allowedModels || [],
            temperature: prompt.configuration?.temperature ?? 0.2,
            maxTokens: prompt.configuration?.maxTokens ?? 4000,
            topP: prompt.configuration?.topP ?? 1,
        });
    }, [prompt, reset]);

    useEffect(() => {
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isDirty) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    if (isLoading) {
        return <p className='text-sm text-gray-500 dark:text-gray-400'>Loading prompt...</p>;
    }

    if (isError || !prompt) {
        return (
            <div>
                <h1 className='text-xl font-bold dark:text-white'>Prompt not found</h1>
                <p className='mt-2 text-sm text-red-600 dark:text-red-400'>
                    {(error as { data?: { message?: string } })?.data?.message || 'Unable to load this prompt.'}
                </p>
                <button
                    type='button'
                    onClick={() => navigate('/prompts')}
                    className='mt-4 text-sm text-primary dark:text-primary-darkMode hover:underline'
                >
                    Back to prompts
                </button>
            </div>
        );
    }

    const id = getPromptId(prompt);
    const isArchived = Boolean(prompt.archived);
    const canModify = canEdit && !isArchived;
    const actionLoading = publishing || archiving || unarchiving || restoring;
    const currentVersion = prompt.version;
    const publishedVersion = prompt.publishedVersion ?? null;

    const getErrorMessage = (err: unknown, fallback: string) =>
        (err as { data?: { message?: string } })?.data?.message ||
        (err as { error?: string })?.error ||
        fallback;

    const confirmLeaveIfDirty = () => {
        if (!isDirty) return true;
        return window.confirm('You have unsaved changes. Leave this page and discard them?');
    };

    const goBack = () => {
        if (!confirmLeaveIfDirty()) return;
        navigate('/prompts');
    };

    const switchTab = (tab: DetailTab) => {
        if (tab !== 'editor' && isDirty) {
            if (!window.confirm('You have unsaved changes. Switch tab and discard them?')) return;
            reset();
        }
        setActiveTab(tab);
    };

    const onSave = async (values: EditFormValues) => {
        if (prompt.version == null) {
            setActionError('Prompt version is missing. Cannot update.');
            return;
        }
        setSaveMessage(null);
        setActionError(null);
        try {
            const body: IUpdatePromptPayload = {
                version: prompt.version,
                name: values.name,
                description: values.description,
                systemPrompt: values.systemPrompt,
                prompt: values.prompt,
                allowedProviders: values.allowedProviders,
                allowedModels: values.allowedModels,
                configuration: {
                    temperature: values.temperature,
                    maxTokens: values.maxTokens,
                    topP: values.topP,
                },
            };
            await updatePrompt({ id, body }).unwrap();
            resetHistory();
            setSaveMessage('Changes saved. Publish when you are ready to go live.');
        } catch (err) {
            setActionError(getErrorMessage(err, 'Failed to save changes. The prompt was left untouched.'));
        }
    };

    const onConfirmAction = async () => {
        const action = confirmAction;
        const versionToRestore = restoreVersion;
        setActionError(null);
        setSaveMessage(null);
        try {
            if (action === 'publish') {
                if (!canPublish) {
                    setActionError('Only tech-team users can publish prompts.');
                    return;
                }
                await publishPrompt(id).unwrap();
                resetHistory();
                setSaveMessage('Prompt published. This version is now live.');
            }
            if (action === 'archive') {
                await archivePrompt(id).unwrap();
                resetHistory();
                setSaveMessage('Prompt archived.');
            }
            if (action === 'unarchive') {
                await unarchivePrompt(id).unwrap();
                resetHistory();
                setSaveMessage('Prompt unarchived.');
            }
            if (action === 'restore' && versionToRestore != null) {
                await restorePromptVersion({ id, version: versionToRestore }).unwrap();
                resetHistory();
                setActiveTab('editor');
                setSaveMessage(
                    `Restored from version ${versionToRestore}. Content was copied into a new version — publish to make it live.`,
                );
            }
        } catch (err) {
            setActionError(
                getErrorMessage(
                    err,
                    action === 'restore'
                        ? 'Restore failed. The prompt was left untouched.'
                        : 'Action failed. The prompt was left untouched.',
                ),
            );
        } finally {
            setConfirmAction(null);
            setRestoreVersion(null);
        }
    };

    const onViewVersion = async (version: number) => {
        setActionError(null);
        setViewLoading(true);
        try {
            const entry = await fetchHistoryVersion({ id, version: Number(version) }).unwrap();
            setViewedVersion({
                ...entry,
                version: entry.version ?? version,
            });
        } catch (err) {
            setActionError(getErrorMessage(err, 'Failed to load version details.'));
        } finally {
            setViewLoading(false);
        }
    };

    return (
        <div>
            <button
                type='button'
                onClick={goBack}
                className='text-sm text-primary dark:text-primary-darkMode hover:underline mb-3'
            >
                ← Back to prompts
            </button>

            <div className='flex flex-wrap items-start justify-between gap-4'>
                <div>
                    <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                        {prompt.name}
                    </h1>
                    <p className='mt-1 text-sm font-mono text-gray-500 dark:text-gray-400'>{prompt.key}</p>
                    <div className='mt-3 flex flex-wrap items-center gap-2'>
                        <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getPromptStatusBadgeClass(prompt)}`}
                        >
                            {getPromptStatusLabel(prompt)}
                        </span>
                        {currentVersion != null && (
                            <span className='text-xs text-gray-600 dark:text-gray-400'>
                                Current version: v{currentVersion}
                            </span>
                        )}
                        <span className='text-xs text-gray-600 dark:text-gray-400'>
                            Published version:{' '}
                            {publishedVersion != null ? `v${publishedVersion}` : 'none'}
                        </span>
                    </div>
                </div>

                <div className='flex flex-wrap gap-2'>
                    {canPublish && canShowPublishAction(prompt) && (
                        <button
                            type='button'
                            onClick={() => setConfirmAction('publish')}
                            className='px-4 py-2 rounded-md text-sm text-white bg-primary-default'
                        >
                            {prompt.published ? 'Publish live' : 'Publish'}
                        </button>
                    )}
                    {canArchive && !isArchived && (
                        <button
                            type='button'
                            onClick={() => setConfirmAction('archive')}
                            className='px-4 py-2 rounded-md text-sm border border-red-300 text-red-700 dark:border-red-700 dark:text-red-400'
                        >
                            Archive
                        </button>
                    )}
                    {canArchive && isArchived && (
                        <button
                            type='button'
                            onClick={() => setConfirmAction('unarchive')}
                            className='px-4 py-2 rounded-md text-sm border border-gray-300 dark:border-gray-700'
                        >
                            Unarchive
                        </button>
                    )}
                </div>
            </div>

            {saveMessage && (
                <p className='mt-4 text-sm text-green-700 dark:text-green-400'>{saveMessage}</p>
            )}
            {actionError && (
                <p className='mt-4 text-sm text-red-600 dark:text-red-400'>{actionError}</p>
            )}
            {isArchived && (
                <div className='mt-4 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300'>
                    This prompt is archived. Unarchive it before editing or publishing.
                </div>
            )}
            {!isArchived && prompt.hasUnpublishedChanges && (
                <div className='mt-4 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300'>
                    {currentVersion != null ? (
                        <>
                            Version {currentVersion} is saved but not live.{' '}
                            {publishedVersion != null
                                ? `Version ${publishedVersion} is serving traffic.`
                                : 'No version is serving traffic yet.'}
                            {!canPublish && ' Only tech-team users can publish.'}
                        </>
                    ) : (
                        'This prompt has unpublished changes.'
                    )}
                </div>
            )}

            <div className='mt-6 border-b border-gray-200 dark:border-borderColor-darkMode'>
                <nav className='flex gap-1' aria-label='Prompt sections'>
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type='button'
                            onClick={() => switchTab(tab.id)}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                activeTab === tab.id
                                    ? 'border-primary-default text-primary-default dark:text-primary-darkMode dark:border-primary-darkMode'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            <div className='mt-6'>
                {activeTab === 'editor' && (
                    <form
                        onSubmit={handleSubmit(onSave)}
                        className='border border-gray-200 dark:border-borderColor-darkMode rounded-xl p-5 max-w-4xl'
                    >
                        <div className='space-y-4'>
                            <div>
                                <label className='block text-sm font-medium mb-1 dark:text-white'>Name</label>
                                <input
                                    disabled={!canModify}
                                    {...register('name', { required: true })}
                                    className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white disabled:opacity-60'
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium mb-1 dark:text-white'>Key</label>
                                <input
                                    value={prompt.key}
                                    disabled
                                    readOnly
                                    className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm font-mono text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                />
                                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Key cannot be changed after creation.</p>
                            </div>
                            <div>
                                <label className='block text-sm font-medium mb-1 dark:text-white'>Description</label>
                                <textarea
                                    rows={2}
                                    disabled={!canModify}
                                    {...register('description')}
                                    className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white disabled:opacity-60'
                                />
                            </div>

                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                <div>
                                    <label className='block text-sm font-medium mb-2 dark:text-white'>Allowed providers</label>
                                    <Controller
                                        name='allowedProviders'
                                        control={control}
                                        rules={{ validate: (v) => (v?.length ?? 0) > 0 || 'Select at least one provider' }}
                                        render={({ field }) => (
                                            <div className='space-y-2'>
                                                {PROVIDER_OPTIONS.map((provider) => (
                                                    <label key={provider} className='flex items-center gap-2 text-sm dark:text-white'>
                                                        <input
                                                            type='checkbox'
                                                            disabled={!canModify}
                                                            checked={field.value.includes(provider)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    field.onChange([...field.value, provider]);
                                                                } else {
                                                                    field.onChange(field.value.filter((p) => p !== provider));
                                                                }
                                                            }}
                                                            className='h-4 w-4'
                                                        />
                                                        {provider}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    />
                                    {errors.allowedProviders && (
                                        <p className='text-xs text-red-600 mt-1'>{errors.allowedProviders.message}</p>
                                    )}
                                </div>
                                <div>
                                    <label className='block text-sm font-medium mb-2 dark:text-white'>Allowed models</label>
                                    <Controller
                                        name='allowedModels'
                                        control={control}
                                        rules={{ validate: (v) => (v?.length ?? 0) > 0 || 'Select at least one model' }}
                                        render={({ field }) => (
                                            <div className='space-y-2 max-h-40 overflow-y-auto'>
                                                {MODEL_OPTIONS.map((model) => (
                                                    <label key={model} className='flex items-center gap-2 text-sm dark:text-white'>
                                                        <input
                                                            type='checkbox'
                                                            disabled={!canModify}
                                                            checked={field.value.includes(model)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    field.onChange([...field.value, model]);
                                                                } else {
                                                                    field.onChange(field.value.filter((m) => m !== model));
                                                                }
                                                            }}
                                                            className='h-4 w-4'
                                                        />
                                                        {model}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    />
                                    {errors.allowedModels && (
                                        <p className='text-xs text-red-600 mt-1'>{errors.allowedModels.message}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className='block text-sm font-medium mb-1 dark:text-white'>System prompt</label>
                                <textarea
                                    rows={4}
                                    disabled={!canModify}
                                    {...register('systemPrompt', { required: true })}
                                    className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white font-mono disabled:opacity-60'
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium mb-1 dark:text-white'>Prompt</label>
                                <textarea
                                    rows={8}
                                    disabled={!canModify}
                                    {...register('prompt', { required: true })}
                                    className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white font-mono disabled:opacity-60'
                                />
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                <div>
                                    <label className='block text-sm font-medium mb-1 dark:text-white'>Temperature</label>
                                    <input
                                        type='number'
                                        step='0.1'
                                        min='0'
                                        max='2'
                                        disabled={!canModify}
                                        {...register('temperature', { valueAsNumber: true, required: true })}
                                        className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white disabled:opacity-60'
                                    />
                                </div>
                                <div>
                                    <label className='block text-sm font-medium mb-1 dark:text-white'>Max tokens</label>
                                    <input
                                        type='number'
                                        min='1'
                                        disabled={!canModify}
                                        {...register('maxTokens', { valueAsNumber: true, required: true })}
                                        className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white disabled:opacity-60'
                                    />
                                </div>
                                <div>
                                    <label className='block text-sm font-medium mb-1 dark:text-white'>Top P</label>
                                    <input
                                        type='number'
                                        step='0.1'
                                        min='0'
                                        max='1'
                                        disabled={!canModify}
                                        {...register('topP', { valueAsNumber: true, required: true })}
                                        className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white disabled:opacity-60'
                                    />
                                </div>
                            </div>
                            {canModify && (
                                <div className='flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-800'>
                                    <p className='text-xs text-gray-500 dark:text-gray-400 mr-auto'>
                                        Saving creates a new draft version. It does not go live.
                                    </p>
                                    <button
                                        type='submit'
                                        disabled={!isDirty || saving}
                                        className='px-4 py-2 rounded-md text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-black text-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-60'
                                    >
                                        {saving ? 'Saving...' : 'Save changes'}
                                    </button>
                                    {canPublish && canShowPublishAction(prompt) && (
                                        <button
                                            type='button'
                                            onClick={() => setConfirmAction('publish')}
                                            className='px-4 py-2 rounded-md text-sm text-white bg-primary-default'
                                        >
                                            {prompt.published ? 'Publish live' : 'Publish'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </form>
                )}

                {activeTab === 'history' && (
                    <div className='max-w-2xl'>
                        <VersionHistory
                            versions={historyRows}
                            currentVersion={prompt.version}
                            currentVersionData={{
                                version: prompt.version,
                                allowedProviders: prompt.allowedProviders,
                                allowedModels: prompt.allowedModels,
                                prompt: prompt.prompt,
                                systemPrompt: prompt.systemPrompt,
                                updatedAt: prompt.updatedAt,
                                updatedBy: prompt.updatedBy,
                            }}
                            loading={historyLoading && historyPage === 1}
                            loadingMore={historyFetching && historyPage > 1}
                            hasMore={hasMoreHistory}
                            canRestore={canModify}
                            restoringVersion={restoring ? restoreVersion : null}
                            onRestore={(version) => {
                                setRestoreVersion(version);
                                setConfirmAction('restore');
                            }}
                            onViewVersion={onViewVersion}
                            onLoadMore={onLoadMoreHistory}
                        />
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className='border border-gray-200 dark:border-borderColor-darkMode rounded-xl p-5 max-w-xl space-y-4'>
                        <div>
                            <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Key</p>
                            <p className='text-sm font-mono dark:text-white mt-1'>{prompt.key}</p>
                        </div>
                        <div>
                            <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Status</p>
                            <p className='text-sm dark:text-white mt-1'>{getPromptStatusLabel(prompt)}</p>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Current version</p>
                                <p className='text-sm dark:text-white mt-1'>
                                    {currentVersion != null ? `v${currentVersion}` : '—'}
                                </p>
                            </div>
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Published version</p>
                                <p className='text-sm dark:text-white mt-1'>
                                    {publishedVersion != null ? `v${publishedVersion}` : 'none'}
                                </p>
                            </div>
                        </div>
                        {prompt.createdAt && (
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Created</p>
                                <p className='text-sm dark:text-white mt-1'>
                                    {moment(prompt.createdAt).format('DD MMM YYYY, HH:mm')}
                                </p>
                            </div>
                        )}
                        {prompt.updatedAt && (
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Updated</p>
                                <p className='text-sm dark:text-white mt-1'>
                                    {moment(prompt.updatedAt).format('DD MMM YYYY, HH:mm')}
                                </p>
                            </div>
                        )}
                        {canArchive && (
                            <div className='pt-4 border-t border-gray-100 dark:border-gray-800'>
                                {!isArchived ? (
                                    <button
                                        type='button'
                                        onClick={() => setConfirmAction('archive')}
                                        className='px-4 py-2 rounded-md text-sm border border-red-300 text-red-700 dark:border-red-700 dark:text-red-400'
                                    >
                                        Archive prompt
                                    </button>
                                ) : (
                                    <button
                                        type='button'
                                        onClick={() => setConfirmAction('unarchive')}
                                        className='px-4 py-2 rounded-md text-sm border border-gray-300 dark:border-gray-700'
                                    >
                                        Unarchive prompt
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ConfirmActionModal
                open={confirmAction === 'publish'}
                title='Publish prompt'
                description='Publishing makes this prompt the live version served to traffic. Continue?'
                confirmLabel='Publish live'
                loading={actionLoading}
                onClose={() => setConfirmAction(null)}
                onConfirm={onConfirmAction}
            />
            <ConfirmActionModal
                open={confirmAction === 'archive'}
                title='Archive prompt'
                description='Archived prompts cannot be edited until unarchived.'
                confirmLabel='Archive'
                destructive
                loading={actionLoading}
                onClose={() => setConfirmAction(null)}
                onConfirm={onConfirmAction}
            />
            <ConfirmActionModal
                open={confirmAction === 'unarchive'}
                title='Unarchive prompt'
                description='This will make the prompt editable again. Continue?'
                confirmLabel='Unarchive'
                loading={actionLoading}
                onClose={() => setConfirmAction(null)}
                onConfirm={onConfirmAction}
            />
            <ConfirmActionModal
                open={confirmAction === 'restore'}
                title='Restore version'
                description={
                    <>
                        <p>
                            Restore version {restoreVersion}? This copies that version&apos;s content into a{' '}
                            <strong>new</strong> version.
                        </p>
                        <ul className='list-disc pl-5 space-y-1'>
                            <li>Full history is kept</li>
                            <li>The live / published version does not change until you publish</li>
                        </ul>
                    </>
                }
                confirmLabel='Restore'
                loading={actionLoading}
                onClose={() => {
                    setConfirmAction(null);
                    setRestoreVersion(null);
                }}
                onConfirm={onConfirmAction}
            />

            <Modal
                open={Boolean(viewedVersion) || viewLoading}
                onClose={() => setViewedVersion(null)}
                title={viewedVersion?.version != null ? `Version ${viewedVersion.version}` : 'Version details'}
                contentContainerStyle='w-full max-w-[640px] !max-h-[85vh] overflow-y-auto'
            >
                {viewLoading && !viewedVersion ? (
                    <p className='text-sm text-gray-500 dark:text-gray-400 pt-2'>Loading version...</p>
                ) : viewedVersion ? (
                    <div className='space-y-4 pt-2'>
                        {viewedVersion.changeType && (
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Change type</p>
                                <p className='text-sm dark:text-white mt-1 capitalize'>
                                    {viewedVersion.changeType.replace(/_/g, ' ')}
                                </p>
                            </div>
                        )}
                        {(viewedVersion.changedBy || viewedVersion.timestamp || viewedVersion.createdAt) && (
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Changed by</p>
                                <p className='text-sm dark:text-white mt-1'>
                                    {viewedVersion.changedBy?.name ||
                                        viewedVersion.changedBy?.username ||
                                        viewedVersion.changedBy?.email ||
                                        '—'}
                                    {(viewedVersion.timestamp || viewedVersion.createdAt) && (
                                        <span className='text-gray-500 dark:text-gray-400'>
                                            {' · '}
                                            {moment(viewedVersion.timestamp || viewedVersion.createdAt).format(
                                                'DD MMM YYYY, HH:mm',
                                            )}
                                        </span>
                                    )}
                                </p>
                                {viewedVersion.changedBy?.email && (
                                    <p className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                                        {viewedVersion.changedBy.email}
                                    </p>
                                )}
                            </div>
                        )}
                        {viewedVersion.restoredFromVersion != null && (
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Restored from</p>
                                <p className='text-sm dark:text-white mt-1'>v{viewedVersion.restoredFromVersion}</p>
                            </div>
                        )}
                        {flattenPromptChanges(resolvePromptChanges(viewedVersion)).length > 0 ? (
                            <div>
                                <p className='text-xs uppercase text-gray-500 dark:text-gray-400 mb-2'>Changes</p>
                                <PromptChangesDiff changes={resolvePromptChanges(viewedVersion)} />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Providers</p>
                                    <p className='text-sm dark:text-white mt-1'>
                                        {(viewedVersion.allowedProviders || []).join(', ') || '—'}
                                    </p>
                                </div>
                                <div>
                                    <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Models</p>
                                    <p className='text-sm dark:text-white mt-1'>
                                        {(viewedVersion.allowedModels || []).join(', ') || '—'}
                                    </p>
                                </div>
                                <div className='grid grid-cols-3 gap-3'>
                                    <div>
                                        <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Temperature</p>
                                        <p className='text-sm dark:text-white mt-1'>
                                            {viewedVersion.configuration?.temperature ?? '—'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Max tokens</p>
                                        <p className='text-sm dark:text-white mt-1'>
                                            {viewedVersion.configuration?.maxTokens ?? '—'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Top P</p>
                                        <p className='text-sm dark:text-white mt-1'>
                                            {viewedVersion.configuration?.topP ?? '—'}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>System prompt</p>
                                    <p className='text-sm dark:text-white mt-1 whitespace-pre-wrap font-mono'>
                                        {viewedVersion.systemPrompt || '—'}
                                    </p>
                                </div>
                                <div>
                                    <p className='text-xs uppercase text-gray-500 dark:text-gray-400'>Prompt</p>
                                    <p className='text-sm dark:text-white mt-1 whitespace-pre-wrap font-mono'>
                                        {viewedVersion.prompt || '—'}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                ) : null}
            </Modal>
        </div>
    );
};

export default PromptDetailPage;
